import React from 'react';
import Head from 'next/head';
import { Shield, FileText } from 'lucide-react';

export default function PrivacyPage() {
  const lastUpdated = 'January 28, 2026';

  return (
    <>
      <Head>
        <title>Privacy Policy | Sizzle</title>
        <meta
          name="description"
          content="Learn how Sizzle handles your data and protects your privacy."
        />
      </Head>
      <div className="min-h-screen bg-gray-50 pt-32 pb-24">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="bg-white rounded-[3rem] p-12 mb-12 shadow-sm border border-gray-100">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center text-primary-600">
                  <Shield className="w-6 h-6" />
                </div>
                <h1 className="text-4xl font-black text-gray-900">Privacy Policy</h1>
              </div>
              <p className="text-gray-600 text-lg">
                Your privacy is important to us. This policy explains how we collect, use, and
                protect your information when you use Sizzle.
              </p>
              <div className="mt-8 pt-8 border-t border-gray-50 flex items-center gap-2 text-sm text-gray-400">
                <FileText className="w-4 h-4" />
                <span>Last Updated: {lastUpdated}</span>
              </div>
            </div>

            {/* Content */}
            <div className="bg-white rounded-[3rem] p-12 shadow-sm border border-gray-100 prose prose-gray max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">1. Information We Collect</h2>
              <p>
                To provide you with the best experience on Sizzle, we collect certain information:
              </p>
              <ul>
                <li>
                  <strong>Account Information:</strong> When you sign up, we collect your name,
                  email address, and profile information.
                </li>
                <li>
                  <strong>Usage Data:</strong> We collect info on how you interact with the
                  platform, including items you view, collections you create, and your search
                  queries.
                </li>
                <li>
                  <strong>Device Information:</strong> We may collect info about the device you're
                  using, including IP address and browser type.
                </li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">
                2. How We Use Your Information
              </h2>
              <p>We use your information to:</p>
              <ul>
                <li>Personalize your discovery experience.</li>
                <li>Allow you to create and manage collections.</li>
                <li>Improve our recommendation algorithms.</li>
                <li>Communicate with you about updates and new features.</li>
                <li>Monitor and protect the security of our services.</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">3. Data Sharing</h2>
              <p>
                We do not sell your personal data to third parties. We may share information with:
              </p>
              <ul>
                <li>
                  <strong>Service Providers:</strong> Partners who help us provide our services
                  (e.g., hosting, analytics).
                </li>
                <li>
                  <strong>Legal Compliance:</strong> If required by law or to protect our rights.
                </li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">4. Your Choices</h2>
              <p>You have control over your data:</p>
              <ul>
                <li>You can update your profile information at any time.</li>
                <li>You can delete your account and all associated data.</li>
                <li>
                  You can choose whether to share your collections publicly or keep them private.
                </li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">5. Security</h2>
              <p>
                We take security seriously and use industry-standard measures to protect your data.
                However, no method of transmission over the internet is 100% secure.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">
                6. Changes to This Policy
              </h2>
              <p>
                We may update this policy from time to time. We will notify you of any significant
                changes by posting the new policy on this page.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mt-12 mb-6">7. Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy, please contact us at:
                <br />
                <a
                  href="mailto:privacy@sizzlecpg.com"
                  className="text-primary-600 font-bold no-underline hover:underline"
                >
                  privacy@sizzlecpg.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
